import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { AddIntegrationsModal } from '@/features/integrations/add-integrations-modal';
import {
  integrationManagementSearchId,
  settingsItemDomId,
} from '@/features/settings/settings-search-dom';
import { SettingsSection } from '@/features/settings/settings-section';
import {
  resolveIntegrationProviderDeepLink,
  useSettingsDeepLinkTarget,
} from '@/features/settings/useSettingsDeepLink';

import { useIntegrationProviders } from './integration-management.query';
import { ProviderDetail } from './provider-detail';
import { ProviderRail } from './provider-rail';

import type {
  IntegrationIssue,
  IntegrationProvider,
  ProviderKey,
} from '@littlebird/integrations';

type ProviderIssue = {
  source: IntegrationIssue['source'];
  message: string;
  retry(): void;
};

/** Tracks the selected provider by key, defaulting to the first provider once
 * any load. If the selected provider's source confirms it's gone, reselect
 * its old slot in the list (now occupied by another provider), else the slot
 * before it, else clear the selection.
 *
 * `deepLinkedKey` overrides that default, once per link. As soon as it names
 * a provider in `providers`, selection jumps to it — before the generic
 * deep-link highlight effect looks for the detail pane's DOM node. After
 * that, `appliedDeepLinkRef` remembers it applied the link, so a later manual
 * selection sticks instead of being overridden again — until the link is
 * consumed (`deepLinkedKey` goes back to `null`), which clears the ref so the
 * same provider key deep-links again the next time it's searched. */
function useSelectedProviderKey(
  providers: readonly IntegrationProvider[],
  deepLinkedKey: ProviderKey | null
): [ProviderKey | null, (key: ProviderKey) => void] {
  const [selectedKey, setSelectedKey] = useState<ProviderKey | null>(null);
  const lastIndexRef = useRef(-1);
  const appliedDeepLinkRef = useRef<ProviderKey | null>(null);

  useEffect(() => {
    if (!deepLinkedKey) {
      appliedDeepLinkRef.current = null;
    } else if (deepLinkedKey !== appliedDeepLinkRef.current) {
      const deepLinkedIndex = providers.findIndex(
        (p) => p.key === deepLinkedKey
      );
      if (deepLinkedIndex !== -1) {
        appliedDeepLinkRef.current = deepLinkedKey;
        setSelectedKey(deepLinkedKey);
        lastIndexRef.current = deepLinkedIndex;
        return;
      }
    }

    const currentIndex = providers.findIndex((p) => p.key === selectedKey);
    if (currentIndex !== -1) {
      lastIndexRef.current = currentIndex;
      return;
    }
    if (selectedKey === null) {
      if (providers.length > 0) {
        // Default to the first provider — but a just-applied deep link may not
        // have committed to selectedKey yet (StrictMode replays the effect
        // before the commit), so prefer it while it's still in the list.
        const appliedIndex = appliedDeepLinkRef.current
          ? providers.findIndex((p) => p.key === appliedDeepLinkRef.current)
          : -1;
        const index = appliedIndex === -1 ? 0 : appliedIndex;
        setSelectedKey(providers[index].key);
        lastIndexRef.current = index;
      }
      return;
    }
    const fallback =
      providers[lastIndexRef.current] ?? providers[lastIndexRef.current - 1];
    setSelectedKey(fallback?.key ?? null);
    lastIndexRef.current = fallback ? providers.indexOf(fallback) : -1;
  }, [providers, selectedKey, deepLinkedKey]);

  return [selectedKey, setSelectedKey];
}

export const IntegrationManagementView = observer(
  function IntegrationManagementView({
    providerKey = null,
  }: {
    providerKey?: ProviderKey | null;
  }) {
    const { providers, isInitialLoading, issues } = useIntegrationProviders();
    const [selectedKey, setSelectedKey] = useSelectedProviderKey(
      providers,
      providerKey
    );
    const selectedProvider =
      providers.find((p) => p.key === selectedKey) ?? null;

    const openAddIntegration = () =>
      ModalsManager.show(AddIntegrationsModal, {});

    const hasIssues = issues.length > 0;
    // With the rail on screen, failures collapse into one pill next to the
    // "Runs everywhere" header — per-source banners only survive on the
    // rail-less loading/empty states, where there is nowhere to pin a pill.
    const refreshIssues = hasIssues
      ? {
          message:
            "Littlebird couldn't refresh some of your integrations, so this page may be out of date.",
          retryAll: () => issues.forEach((issue) => issue.retry()),
        }
      : null;
    const issueBanners = hasIssues && (
      <YStack className="shrink-0 gap-2 px-4 pt-4">
        {issues.map((issue) => (
          <IssueBanner key={`${issue.source}:${issue.message}`} issue={issue} />
        ))}
      </YStack>
    );

    const title = <SettingsSection.Title>Integrations</SettingsSection.Title>;

    if (isInitialLoading && providers.length === 0) {
      return (
        <YStack className="h-full min-h-0">
          {title}
          {issueBanners}
          <YStack center className="flex-1 min-h-0 gap-2">
            <Icons.loader className="size-4 animate-spin text-foreground-muted" />
            <p className="text-foreground-muted text-xs">
              Loading integrations...
            </p>
          </YStack>
        </YStack>
      );
    }

    if (providers.length === 0) {
      return (
        <YStack className="h-full min-h-0">
          {title}
          {issueBanners}
          <YStack
            center
            className="flex-1 min-h-0 gap-4 py-16 px-6 text-center"
          >
            <p className="text-foreground text-sm">No integrations connected</p>
            {/* Source issues already explain the failure above — the
               encouragement copy would otherwise read as "you have nothing
               connected" when we actually just failed to load what's there. */}
            {!hasIssues && (
              <p className="text-foreground-muted text-xs">
                Connect your apps to let Littlebird work across them.
              </p>
            )}
            <Button size="lg" onClick={openAddIntegration}>
              <Icons.plus className="size-4" />
              Add integration
            </Button>
          </YStack>
        </YStack>
      );
    }

    return (
      <YStack className="h-full min-h-0">
        {title}
        <XStack className="flex-1 min-h-0">
          <ProviderRail
            providers={providers}
            selectedKey={selectedKey}
            onSelect={setSelectedKey}
            refreshIssues={refreshIssues}
          />
          <YStack
            className="flex-1 min-w-0 min-h-0"
            id={
              selectedProvider
                ? settingsItemDomId(
                    integrationManagementSearchId(selectedProvider.key)
                  )
                : undefined
            }
          >
            {/* Keyed by provider so switching selection remounts the whole
               detail pane — including `useProviderToolsPanel`'s pending-write
               and failure state, which must not carry over onto the next
               provider's tools. */}
            {selectedProvider && (
              <ProviderDetail
                key={selectedProvider.key}
                provider={selectedProvider}
              />
            )}
          </YStack>
        </XStack>
      </YStack>
    );
  }
);

export const IntegrationManagementSettings = observer(
  function IntegrationManagementSettings() {
    const { section, item } = useSettingsDeepLinkTarget();
    return (
      <IntegrationManagementView
        providerKey={resolveIntegrationProviderDeepLink(section, item)}
      />
    );
  }
);

function IssueBanner({ issue }: { issue: ProviderIssue }) {
  return (
    <XStack className="items-center justify-between gap-4 border border-border dark:border-border-strong bg-surface rounded-lg px-4 py-3">
      <p className="text-foreground-muted text-xs">{issue.message}</p>
      <Button size="sm" variant="secondary" onClick={issue.retry}>
        Retry
      </Button>
    </XStack>
  );
}
